import React from 'react'
import type { SchoolInfoProps } from '@/features/enrollment'

export default function SchoolInfo({ enrollmentData, updateEnrollmentData, errors, isCurrentlyInSchool }: SchoolInfoProps) {
  return (
    <>
      {/* School Status */}
      <div>
        <label className="block text-sm font-medium text-accent-900 mb-2">
          Is Injured Party currently going to school? <span className="text-red-500">*</span>
        </label>
        <div className="flex items-center space-x-4">
          <label className="flex items-center">
            <input 
              type="radio" 
              name="isCurrentlyInSchool" 
              value="true" 
              checked={enrollmentData.isCurrentlyInSchool === true} 
              onChange={() => updateEnrollmentData('isCurrentlyInSchool', true)} 
              className="form-radio text-primary-600" 
            />
            <span className="ml-2 text-accent-700">Yes</span>
          </label>
          <label className="flex items-center">
            <input 
              type="radio" 
              name="isCurrentlyInSchool" 
              value="false" 
              checked={enrollmentData.isCurrentlyInSchool === false} 
              onChange={() => updateEnrollmentData('isCurrentlyInSchool', false)} 
              className="form-radio text-primary-600" 
            />
            <span className="ml-2 text-accent-700">No</span>
          </label>
        </div>
        {errors.isCurrentlyInSchool && (
          <p className="mt-1 text-sm text-red-600">{errors.isCurrentlyInSchool}</p>
        )}
      </div>

      {/* School Information (conditional) */}
      {enrollmentData.isCurrentlyInSchool === true && (
        <div className="space-y-4 p-4 bg-accent-50 rounded-lg">
          <div>
            <label className="block text-sm font-medium text-accent-900 mb-2">
              School Name <span className="text-red-500">*</span>
            </label>
            <input 
              type="text" 
              name="schoolName"
              className={`input-field w-full ${errors.schoolName ? 'border-red-500' : ''}`} 
              value={enrollmentData.schoolName || ''} 
              onChange={(e) => updateEnrollmentData('schoolName', e.target.value)} 
            />
            {errors.schoolName && (
              <p className="mt-1 text-sm text-red-600">{errors.schoolName}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-accent-900 mb-2">
              School Address <span className="text-red-500">*</span>
            </label>
            <div className="space-y-3">
              <input 
                type="text" 
                className={`input-field w-full ${errors.schoolAddress ? 'border-red-500' : ''}`} 
                placeholder="Address Line 1" 
                value={enrollmentData.schoolAddress || ''} 
                onChange={(e) => updateEnrollmentData('schoolAddress', e.target.value)} 
              />
              {errors.schoolAddress && (
                <p className="text-sm text-red-600">{errors.schoolAddress}</p>
              )}
              <input 
                type="text" 
                className="input-field w-full" 
                placeholder="Address Line 2 (optional)" 
                value={enrollmentData.schoolAddress2 || ''} 
                onChange={(e) => updateEnrollmentData('schoolAddress2', e.target.value)} 
              />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <input 
                  type="text" 
                  className={`input-field ${errors.schoolCity ? 'border-red-500' : ''}`} 
                  placeholder="City" 
                  value={enrollmentData.schoolCity || ''} 
                  onChange={(e) => updateEnrollmentData('schoolCity', e.target.value)} 
                />
                <select 
                  className={`input-field ${errors.schoolState ? 'border-red-500' : ''}`} 
                  value={enrollmentData.schoolState || ''} 
                  onChange={(e) => updateEnrollmentData('schoolState', e.target.value)}
                >
                  <option value="">Select State</option>
                  <option value="AL">Alabama</option>
                  <option value="AK">Alaska</option>
                  <option value="AZ">Arizona</option>
                  <option value="AR">Arkansas</option>
                  <option value="CA">California</option>
                  <option value="CO">Colorado</option>
                  <option value="CT">Connecticut</option>
                  <option value="DE">Delaware</option>
                  <option value="DC">District of Columbia</option>
                  <option value="FL">Florida</option>
                  <option value="GA">Georgia</option>
                  <option value="HI">Hawaii</option>
                  <option value="ID">Idaho</option>
                  <option value="IL">Illinois</option>
                  <option value="IN">Indiana</option>
                  <option value="IA">Iowa</option>
                  <option value="KS">Kansas</option>
                  <option value="KY">Kentucky</option>
                  <option value="LA">Louisiana</option>
                  <option value="ME">Maine</option>
                  <option value="MD">Maryland</option>
                  <option value="MA">Massachusetts</option>
                  <option value="MI">Michigan</option>
                  <option value="MN">Minnesota</option>
                  <option value="MS">Mississippi</option>
                  <option value="MO">Missouri</option>
                  <option value="MT">Montana</option>
                  <option value="NE">Nebraska</option>
                  <option value="NV">Nevada</option>
                  <option value="NH">New Hampshire</option>
                  <option value="NJ">New Jersey</option>
                  <option value="NM">New Mexico</option>
                  <option value="NY">New York</option>
                  <option value="NC">North Carolina</option>
                  <option value="ND">North Dakota</option>
                  <option value="OH">Ohio</option>
                  <option value="OK">Oklahoma</option>
                  <option value="OR">Oregon</option>
                  <option value="PA">Pennsylvania</option>
                  <option value="RI">Rhode Island</option>
                  <option value="SC">South Carolina</option>
                  <option value="SD">South Dakota</option>
                  <option value="TN">Tennessee</option>
                  <option value="TX">Texas</option>
                  <option value="UT">Utah</option>
                  <option value="VT">Vermont</option>
                  <option value="VA">Virginia</option>
                  <option value="WA">Washington</option>
                  <option value="WV">West Virginia</option>
                  <option value="WI">Wisconsin</option>
                  <option value="WY">Wyoming</option>
                </select>
                <input 
                  type="text" 
                  className={`input-field ${errors.schoolZipCode ? 'border-red-500' : ''}`} 
                  placeholder="ZIP Code" 
                  value={enrollmentData.schoolZipCode || ''} 
                  onChange={(e) => updateEnrollmentData('schoolZipCode', e.target.value)} 
                />
              </div>
              {(errors.schoolCity || errors.schoolState || errors.schoolZipCode) && (
                <div className="space-y-1">
                  {errors.schoolCity && <p className="text-sm text-red-600">{errors.schoolCity}</p>}
                  {errors.schoolState && <p className="text-sm text-red-600">{errors.schoolState}</p>}
                  {errors.schoolZipCode && <p className="text-sm text-red-600">{errors.schoolZipCode}</p>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}